<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, toRef, watch } from "vue";
import { useTranslation } from "i18next-vue";
import { useQueryClient } from "@tanstack/vue-query";
import APIClient from "@/APIClient";
import { QueryKey } from "@/stores/queries/query-keys";
import { trackTab } from "@/analytics/drawing-analytics";
import {
  clearTypeFilter,
  consumeObservationFramePick,
  observationFramePick,
  setActiveTab,
  viewMode,
  type ObservationsDrawerTab,
} from "@/stores/ObservationsPanelState";
import type { FieldObservation } from "@/types/field-observations.types";
import {
  useGetFieldObservations,
  useGetFieldParts,
} from "@/stores/queries/field-observations.query";
import { useDeviceState } from "@/stores/DeviceState";
import { usePlanAnalysisState } from "@/stores/PlanAnalysisState";
import { useDrawingDiagnosticLink } from "@/composable/useDrawingDiagnosticLink";
import { useObservationPlanLinks } from "@/composable/useObservationPlanLinks";
import { useObservationFramesFeed } from "@/composable/useObservationFrames";
import { useObservationPartAssign } from "@/composable/useObservationPartAssign";
import {
  buildObservationLetters,
  sortByCreation,
} from "@/utils/observation-plan";
import DrawerMenuContent from "../DrawerMenuContent.vue";
import ObservationsDrawerTabs from "./ObservationsDrawerTabs.vue";
import ObservationsNotLinked from "./ObservationsNotLinked.vue";
import ObservationsPartsPlan from "./ObservationsPartsPlan.vue";
import DrawingLinkDialog from "./DrawingLinkDialog.vue";
import DevPurgeButton from "@/components/canvas/analysis/DevPurgeButton.vue";
import DRBtn from "@/components/UI/DRBtn.vue";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";

/**
 * Onglet « Parties » du volet Observations : classer les observations de
 * l'ouvrage par partie, depuis le plan. Celles qui ont des éléments sur ce
 * plan y gardent leur cadre bleu et leur lettre ; une partie sélectionnée
 * dans l'arborescence, toucher un badge (ou tracer une zone) rattache
 * l'observation — enregistré en ligne à chaque geste. Les observations sans
 * élément sur ce plan se classent depuis leur fiche (onglet Observations).
 */

const props = defineProps<{
  drawingId: number;
  /** Onglets proposés par le volet (un seul = pas de barre d'onglets). */
  tabs: ObservationsDrawerTab[];
}>();

defineEmits<{
  (e: "close"): void;
}>();

const { t } = useTranslation();
const deviceState = useDeviceState();
const state = usePlanAnalysisState();

const { isLoadingDrawing, diagnosticId } = useDrawingDiagnosticLink(
  props.drawingId,
);
const showLinkDialog = ref(false);

function openLinkDialog(): void {
  trackTab("observations", "diagnostic_link_opened");
  showLinkDialog.value = true;
}

// Le classement s'enregistre en ligne : la connexion est revérifiée à
// l'ouverture de l'onglet (l'état vaut null au démarrage), puis à chaque
// bascule signalée par le navigateur — l'outil dessin ne la surveille pas en
// continu.
const recheckConnection = (): void => {
  void deviceState.checkIfOnline();
};
onMounted(() => {
  recheckConnection();
  window.addEventListener("online", recheckConnection);
  window.addEventListener("offline", recheckConnection);
});
onUnmounted(() => {
  window.removeEventListener("online", recheckConnection);
  window.removeEventListener("offline", recheckConnection);
});

const isOffline = computed(() => deviceState.isOnline === false);

// ── Observations et parties de l'ouvrage ────────────────────────────────────

const { data: observations } = useGetFieldObservations(toRef(diagnosticId));
// Le pied « Ajouter une partie / Insérer un modèle » n'existe que tant que
// l'ouvrage n'a aucune partie : ensuite tout passe par les « + » de l'arbre.
// Jugé sur la liste chargée — il ne clignote pas pendant sa lecture.
const { data: fieldParts } = useGetFieldParts(toRef(diagnosticId));
const hasNoPart = computed(() => fieldParts.value?.length === 0);

const sortedObservations = computed(() =>
  sortByCreation(observations.value ?? []),
);
/** Mêmes lettres que dans l'onglet Observations (A = la plus ancienne). */
const letters = computed(() =>
  buildObservationLetters(observations.value ?? []),
);

// Un enregistrement refusé : c'est peut-être la connexion qui est tombée
// sans que le navigateur l'ait vu (serveur injoignable).
const { partIdOf, assign, toggle } = useObservationPartAssign(
  toRef(diagnosticId),
  recheckConnection,
);

// ── Cadres bleus sur le plan ────────────────────────────────────────────────
// En mode association, ceux de la partie sélectionnée ressortent en vert.

const { entitiesByObservation } = useObservationPlanLinks();
/** Observation mise en avant sur le plan (ligne ou badge touché). */
const currentId = ref<number | null>(null);

const { bboxOf, zoomOn } = useObservationFramesFeed({
  observations,
  letters,
  entitiesByObservation,
  isCurrent: (observation) => observation.id === currentId.value,
  assigned: (observation) =>
    state.assignPartId === null
      ? null
      : partIdOf(observation) === state.assignPartId,
});

const onPlanIds = computed(
  () =>
    new Set(
      sortedObservations.value
        .filter((observation) => bboxOf(observation) !== null)
        .map((observation) => observation.id),
    ),
);

/** Ligne touchée sous sa partie : l'observation est montrée sur le plan. */
const locateObservation = (observation: FieldObservation): void => {
  currentId.value = observation.id;
  zoomOn(observation);
};

// Changer de partie cible repart d'un plan sans observation mise en avant.
watch(
  () => state.assignPartId,
  () => {
    currentId.value = null;
  },
);

// Geste sur les cadres du plan. Partie sélectionnée : un badge rattache ou
// détache ; une zone rattache seulement — elle ne retire jamais une
// observation de la partie sélectionnée. Dans les deux cas, une observation
// classée dans une AUTRE partie change de partie. Sans partie sélectionnée :
// le badge met l'observation en avant, un second tap éteint le projecteur.
watch(observationFramePick, (pick) => {
  if (!pick) return;
  consumeObservationFramePick();
  const targets = (observations.value ?? []).filter(
    (observation) =>
      !!observation.localId && pick.ids.includes(observation.localId),
  );
  const partId = state.assignPartId;
  if (partId === null) {
    if (pick.kind !== "badge") return;
    const tappedId = targets[0]?.id ?? null;
    currentId.value = tappedId === currentId.value ? null : tappedId;
    return;
  }
  if (isOffline.value) return;
  // Le classement reprend : plus d'observation mise en avant (sa ligne peut
  // quitter la partie affichée, le projecteur resterait sur un cadre orphelin).
  currentId.value = null;
  for (const observation of targets) {
    if (pick.kind === "zone") assign(observation, partId);
    else toggle(observation, partId);
  }
});

// Quitter l'onglet (démontage) met fin au mode association.
onUnmounted(() => {
  state.setAssignPart(null);
  consumeObservationFramePick();
});

/**
 * Les observations hors plan se classent depuis leur fiche : direction la
 * liste triée par partie, tous types affichés — un filtre resté actif
 * pourrait masquer justement celles qu'on vient chercher.
 */
const openListByPart = (): void => {
  clearTypeFilter();
  viewMode.value = "part";
  setActiveTab("observations");
};

const plan = ref<InstanceType<typeof ObservationsPartsPlan> | null>(null);

// ── Outil de développement : repartir d'un ouvrage sans partie ──────────────

const queryClient = useQueryClient();

/**
 * Supprime toutes les parties de l'ouvrage. Le serveur laisse leurs
 * observations en place, sans partie.
 */
const purgeParts = async (): Promise<void> => {
  state.setAssignPart(null);
  try {
    await Promise.all(
      (fieldParts.value ?? []).map((part) => APIClient.deleteFieldPart(part.id)),
    );
  } finally {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [QueryKey.FIELD_PARTS] }),
      queryClient.invalidateQueries({ queryKey: [QueryKey.FIELD_OBSERVATIONS] }),
    ]);
  }
};
</script>

<template>
  <DrawerMenuContent
    :label="t('canvas:observations.title')"
    :description="t('canvas:observations.parts.description')"
    :actions="diagnosticId > 0 && hasNoPart"
  >
    <template v-if="tabs.length > 1" #header>
      <ObservationsDrawerTabs :tabs="tabs" :diagnostic-id="diagnosticId" />
    </template>

    <!-- Chargement du lien plan↔ouvrage -->
    <div v-if="isLoadingDrawing" class="text-center py-5">
      <span class="spinner-border spinner-border-sm" />
    </div>

    <!-- Plan non lié à un ouvrage -->
    <ObservationsNotLinked
      v-else-if="!diagnosticId"
      :disabled="isOffline"
      @link="openLinkDialog()"
    />

    <template v-else>
      <!-- Les parties s'enregistrent en ligne : rien à classer hors connexion -->
      <div v-if="isOffline" class="offline-note d-flex align-items-center gap-2 mb-2">
        <PhosphorIcon name="wifi-slash" :size="16" class="flex-shrink-0" />
        <span>{{ t("canvas:observations.parts.offline") }}</span>
      </div>

      <ObservationsPartsPlan
        ref="plan"
        :diagnostic-id="diagnosticId"
        :observations="sortedObservations"
        :letters="letters"
        :part-id-of="partIdOf"
        :on-plan-ids="onPlanIds"
        :current-id="currentId"
        :disabled="isOffline"
        @locate="locateObservation"
        @open-list="openListByPart"
      >
        <template #tools>
          <DevPurgeButton
            :label="t('canvas:observations.dev.purgeParts')"
            :confirm-message="t('canvas:observations.dev.purgePartsMessage')"
            :disabled="!fieldParts?.length"
            :action="purgeParts"
          />
        </template>
      </ObservationsPartsPlan>
    </template>

    <!-- Pied : première partie de l'ouvrage (ensuite, les « + » de l'arbre) -->
    <template #actions>
      <div class="d-flex justify-content-center gap-2">
        <DRBtn
          icon="bi-plus-lg"
          :label="t('canvas:observations.part.addRoot')"
          variant="primary"
          size="sm"
          :disabled="isOffline"
          @click="plan?.addRootPart()"
        />
        <DRBtn
          icon="bi-folder2-open"
          :label="t('canvas:observations.part.insertTemplate')"
          variant="outline-white"
          size="sm"
          :disabled="isOffline"
          @click="plan?.insertTemplate()"
        />
      </div>
    </template>
  </DrawerMenuContent>

  <DrawingLinkDialog v-model="showLinkDialog" :drawing-id="drawingId" />
</template>

<style lang="scss" scoped>
.offline-note {
  font-size: 0.78rem;
  border: 1px solid var(--canvas-border);
  border-radius: 6px;
  padding: 8px;
  color: var(--bs-warning);
}
</style>
